import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { cn } from "@/utils/cn";

interface InfoTooltipProps {
  text: string;
  /** Extra class applied to the tooltip bubble — use "nightguest-tooltip-content" for the night-guest variant */
  contentClassName?: string;
  /**
   * Where the bubble appears relative to the trigger.
   * "top" (default) — above; use "bottom" inside sticky/overflow-hidden containers
   * where upward tooltips get clipped (e.g. table column headers).
   */
  placement?: "top" | "bottom";
  /** Render tooltip into document.body to avoid clipping in overflow/sticky containers. */
  renderInPortal?: boolean;
  /** Custom trigger element. When omitted the default ℹ Info icon is shown. */
  children?: React.ReactNode;
}

export function InfoTooltip({ text, contentClassName, placement = "top", renderInPortal = false, children }: InfoTooltipProps) {
  const positionCls = placement === "bottom" ? "top-full mt-1" : "bottom-full mb-1";
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!renderInPortal || !isOpen) return;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setCoords({
        left: rect.left + rect.width / 2,
        top: placement === "bottom" ? rect.bottom + 4 : rect.top - 4,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, placement, renderInPortal]);

  const tooltipBody = (
    <>
      {text.split("\n").map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </>
  );

  return (
    <span
      ref={triggerRef}
      className="group relative inline-flex shrink-0 items-center"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
    >
      {children ?? <Info className="h-3 w-3 cursor-help text-slate-400" aria-label={text} />}

      {!renderInPortal && (
        <span
          role="tooltip"
          className={cn(
            "pointer-events-none absolute left-1/2 z-50 w-60 -translate-x-1/2",
            positionCls,
            "bg-black px-3 py-2 text-white shadow-none",
            "custom-tooltip-content",
            "invisible opacity-0 transition-opacity group-hover:visible group-hover:opacity-100",
            contentClassName
          )}
          style={{ borderRadius: 0 }}
        >
          {tooltipBody}
        </span>
      )}

      {renderInPortal && isOpen && coords && typeof document !== "undefined" && createPortal(
        <span
          role="tooltip"
          className={cn(
            "pointer-events-none fixed z-[2000] w-60 -translate-x-1/2",
            placement === "top" ? "-translate-y-full" : "",
            "bg-black px-3 py-2 text-white shadow-none",
            "custom-tooltip-content",
            contentClassName
          )}
          style={{
            left: coords.left,
            top: coords.top,
            borderRadius: 0,
          }}
        >
          {tooltipBody}
        </span>,
        document.body
      )}
    </span>
  );
}
